import { useCallback, useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { asyncRpcQuery } from 'ducks/ai/queries'

import {
  doAiRequest,
  doCancelAiResult,
  doHidePrompt,
  doClearState,
  getAiType,
  doFetchJobResult,
  doAiFailed,
  doAiEnd,
} from './operations'
import { findJobId } from './utils'
import {
  selectComposerRequestAi,
  selectConversationRequestAi,
  selectInstantReplyAi,
} from './selectors'
import { COMPOSER_TYPE, CONVERSATION_TYPE, INSTANT_REPLY_TYPE } from './types'

const TIMEOUT = 1000

// HACK (jscheel): this is bad, we need to refactor to just have a request id that drives everything in the state
const AI_REQUEST_TYPE_SELECT_MAP = {
  [COMPOSER_TYPE]: selectComposerRequestAi,
  [INSTANT_REPLY_TYPE]: selectInstantReplyAi,
  [CONVERSATION_TYPE]: selectConversationRequestAi,
}

export const useAi = aiRequestType => {
  const dispatch = useDispatch()
  const resultCheckTimeoutRef = useRef(null)
  const requestAborted = useRef(false)
  const requestState = useSelector(AI_REQUEST_TYPE_SELECT_MAP[aiRequestType])

  // FIXME (jscheel && kevinrademan && jdarnok): As discussed, this function
  // needs heavy refactoring so that its logic lives in operations. The hook
  // should ultimate just expose these operations. In the interest of time, we
  // are not full refactoring for now (save for the tweaks in the PR that also
  // added this note).
  const requestAndWait = useCallback(
    async (variables, options = {}) => {
      const mergedOptions = { ...options, requestType: aiRequestType }
      let retVal
      requestAborted.current = false

      try {
        const result = await dispatch(
          doAiRequest(asyncRpcQuery, variables, mergedOptions)
        )
        if (requestAborted.current) {
          return undefined
        }
        const resultJobId = findJobId(result)
        const pollForResult = async () => {
          const jobResult = await dispatch(
            doFetchJobResult(resultJobId, mergedOptions)
          )
          if (requestAborted.current) {
            return undefined
          }
          const { state, value } = jobResult?.ephemeralJobResult || {}
          if (state === 'COMPLETED') {
            return value
          }
          if (state === 'FAILED' || state === undefined) {
            throw new Error('Job failed')
          }
          await new Promise(resolve => {
            clearTimeout(resultCheckTimeoutRef.current)
            resultCheckTimeoutRef.current = setTimeout(resolve, TIMEOUT)
          })
          if (requestAborted.current) {
            return undefined
          }
          const ret = await pollForResult()
          if (requestAborted.current) {
            return undefined
          }
          return ret
        }
        retVal = await pollForResult()
      } catch (e) {
        dispatch(doAiFailed(mergedOptions.requestType))
        throw e // If you want to notify the caller about the failure
      }
      dispatch(
        doAiEnd(
          getAiType(mergedOptions.requestType),
          variables,
          mergedOptions,
          retVal
        )
      )
      return retVal
    },
    [dispatch, aiRequestType, requestAborted]
  )

  const rateResponse = useCallback(() => {
    // TODO: Need to wire this up to some backend rating mechanisim
  }, [])

  useEffect(
    () => {
      const timeoutId = resultCheckTimeoutRef.current
      return () => {
        clearTimeout(timeoutId)
      }
    },
    [resultCheckTimeoutRef]
  )

  const abortResult = useCallback(
    () => {
      dispatch(doCancelAiResult(aiRequestType))
    },
    [dispatch, aiRequestType]
  )

  const hidePrompt = useCallback(
    () => {
      dispatch(doHidePrompt(aiRequestType))
    },
    [dispatch, aiRequestType]
  )

  const clearState = useCallback(
    () => {
      dispatch(doClearState(aiRequestType))
    },
    [dispatch, aiRequestType]
  )

  const cancelRequest = useCallback(
    () => {
      clearTimeout(resultCheckTimeoutRef.current)
      requestAborted.current = true
      resultCheckTimeoutRef.current = null
      dispatch(doClearState(aiRequestType))
    },
    [resultCheckTimeoutRef, requestAborted, dispatch, aiRequestType]
  )

  const retryRequest = useCallback(
    async () => {
      const lastRequest = requestState.lastRequest
      if (!lastRequest || !lastRequest.variables || !lastRequest.options) {
        throw new Error('Tried to retry without a proper lastRequest state')
      }

      return requestAndWait(lastRequest.variables, lastRequest.options)
    },
    [requestState, requestAndWait]
  )

  return {
    requestAndWait,
    rateResponse,
    abortResult,
    hidePrompt,
    clearState,
    cancelRequest,
    retryRequest,
    requestState,
  }
}
